import {
  definePreset,
  DIRECTION_SCHEMA,
  kf,
  rad,
  SLIDE_VECTORS,
} from "./helpers";

import type { AnimationPreset } from "./types";

/** Subtle micro-interaction loops — small, quick, UI-polish animations. */
export const microPresets: AnimationPreset[] = [
  definePreset(
    "breathe",
    "Breathe",
    "micro",
    {
      params: { scale: 1.05 },
      schema: [{ key: "scale", label: "Scale", min: 1, max: 1.5, step: 0.01 }],
      duration: 2400,
      repeat: 2,
    },
    (tl, target, _element, cfg) => {
      const s = cfg.params.scale ?? 1.05;
      tl.to(
        target,
        kf(cfg, { scaleX: [1, s, 1], scaleY: [1, s, 1] }, "sine.inOut"),
        0,
      );
    },
  ),
  definePreset(
    "pop",
    "Pop",
    "micro",
    {
      params: { scale: 1.18 },
      schema: [{ key: "scale", label: "Scale", min: 1, max: 2, step: 0.02 }],
      duration: 400,
    },
    (tl, target, _element, cfg) => {
      const s = cfg.params.scale ?? 1.18;
      const scale = [1, s, 0.96, 1];
      tl.to(target, kf(cfg, { scaleX: scale, scaleY: scale }, "sine.out"), 0);
    },
  ),
  definePreset(
    "blink",
    "Blink",
    "micro",
    {
      params: { min: 0.3 },
      schema: [
        { key: "min", label: "Min opacity", min: 0, max: 0.9, step: 0.05 },
      ],
      duration: 350,
    },
    (tl, target, _element, cfg) => {
      tl.to(target, kf(cfg, { opacity: [1, cfg.params.min ?? 0.3, 1] }), 0);
    },
  ),
  definePreset(
    "vibrate",
    "Vibrate",
    "micro",
    {
      params: { amplitude: 2 },
      schema: [
        { key: "amplitude", label: "Amplitude", min: 1, max: 20, step: 1 },
      ],
      duration: 300,
    },
    (tl, target, _element, cfg) => {
      const a = cfg.params.amplitude ?? 2;
      tl.to(target, kf(cfg, { dx: [0, -a, a, -a, a, -a, a, 0] }, "none"), 0);
    },
  ),
  definePreset(
    "nudge",
    "Nudge",
    "micro",
    {
      params: { direction: 1, distance: 12 },
      schema: [
        DIRECTION_SCHEMA,
        { key: "distance", label: "Distance px", min: 2, max: 100, step: 2 },
      ],
      duration: 450,
    },
    (tl, target, _element, cfg) => {
      const [vx, vy] =
        SLIDE_VECTORS[cfg.params.direction ?? 1] ?? SLIDE_VECTORS[1];
      const d = cfg.params.distance ?? 12;
      tl.to(
        target,
        kf(cfg, { dx: [0, vx * d, 0], dy: [0, vy * d, 0] }, "sine.inOut"),
        0,
      );
    },
  ),
  definePreset(
    "tilt",
    "Tilt",
    "micro",
    {
      params: { degrees: 8 },
      schema: [{ key: "degrees", label: "Degrees", min: 1, max: 45, step: 1 }],
      duration: 500,
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 8;
      tl.to(
        target,
        kf(cfg, { angleDelta: [0, d, 0].map(rad) }, "sine.inOut"),
        0,
      );
    },
  ),
  definePreset(
    "bob",
    "Bob",
    "micro",
    {
      params: { distance: 6 },
      schema: [
        { key: "distance", label: "Distance px", min: 1, max: 50, step: 1 },
      ],
      duration: 1200,
      repeat: 2,
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.distance ?? 6;
      tl.to(target, kf(cfg, { dy: [0, -d, 0] }, "sine.inOut"), 0);
    },
  ),
];
